import { COOKING_ORDER, type CookedResult, type CookingMethod } from '@gramas/shared'
import { DisclosureRow, Pill } from '@gramas/ui'
import { useState } from 'react'

export const COOKING_LABEL: Readonly<Record<CookingMethod, string>> = {
  raw: 'Žalias / nekepta',
  boiled: 'Virtas',
  steamed: 'Garuose',
  oven: 'Orkaitėje',
  grilled: 'Ant grilio',
  pan: 'Keptas keptuvėje',
  deep_fried: 'Gruzdintas',
}

/** Short enough for a chip; the row above spells it out. */
export const CHIP_LABEL: Readonly<Record<CookingMethod, string>> = {
  ...COOKING_LABEL,
  raw: 'Žalias',
  pan: 'Keptuvėje',
}

export interface CookingPickerProps {
  value: CookingMethod
  onChange: (method: CookingMethod) => void
  /** Shown once a method other than raw is picked, so the maths is visible. */
  cooked: CookedResult | null
  /** Open on the chips rather than the summary row. */
  defaultOpen?: boolean
}

/**
 * How it was cooked, on every weighed food rather than on a guessed few.
 *
 * The first version only appeared when the product's name matched a list of
 * cookable words, which meant it was missing on "broilerio file" and on every
 * own-brand packet whose name says the shop and not the animal — a control that
 * exists but cannot be found is worse than one that is simply always there.
 *
 * So it is always there, and it costs nothing: one line that reads "Žalias /
 * nekepta" and is ignored by anyone who did not cook. The word list survives
 * only to decide whether the line opens on its options, which is a matter of
 * saving a tap on a chicken breast rather than of whether the feature exists.
 *
 * The rule underneath is worth restating: cooking removes water, not energy, so
 * weighing raw is the accurate measurement and the only real arithmetic is the
 * oil that went into the pan.
 */
export function CookingPicker({
  value,
  onChange,
  cooked,
  defaultOpen = false,
}: CookingPickerProps) {
  const [open, setOpen] = useState(defaultOpen)

  if (!open) {
    return (
      <DisclosureRow label="Gaminimas" value={COOKING_LABEL[value]} onOpen={() => setOpen(true)} />
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm text-mute">Gaminimas — sveriama žalia</span>
      <div className="flex flex-wrap gap-1.5">
        {COOKING_ORDER.map((option) => (
          <Pill key={option} active={option === value} onClick={() => onChange(option)}>
            {CHIP_LABEL[option]}
          </Pill>
        ))}
      </div>
      {cooked && value !== 'raw' && (
        <p className="tnum text-xs text-mute">
          Iškepus apie {cooked.cookedWeightG} g
          {cooked.addedFatKcal > 0 ? ` · aliejus +${cooked.addedFatKcal} kcal` : ''}
        </p>
      )}
    </div>
  )
}
